export interface PublicSearchHit {
  title: string
  url: string
  snippet: string
}

export type PublicSearchResult
  = { status: 'ok', query: string, hits: PublicSearchHit[] }
    | { status: 'INVALID_INPUT' | 'UNAVAILABLE' | 'TIMEOUT', query: string, message: string }

const searchTimeoutMs = 10_000

/**
 * Builds the fixed Wikipedia search URL.
 * The model supplies only the query. The host stays on this endpoint.
 *
 * @example
 * wikipediaSearchUrl('Velis')
 * // => 'https://en.wikipedia.org/w/api.php?action=opensearch&format=json&origin=*&limit=5&search=Velis'
 */
export function wikipediaSearchUrl(query: string) {
  const url = new URL('https://en.wikipedia.org/w/api.php')
  url.searchParams.set('action', 'opensearch')
  url.searchParams.set('format', 'json')
  url.searchParams.set('origin', '*')
  url.searchParams.set('limit', '5')
  url.searchParams.set('search', query)
  return url.toString()
}

/**
 * Reads a Wikipedia OpenSearch payload into hits.
 *
 * @example
 * parseWikipediaOpenSearch(['Velis', ['Velis'], ['A project'], ['https://en.wikipedia.org/wiki/Velis']])
 * // => [{ title: 'Velis', url: 'https://en.wikipedia.org/wiki/Velis', snippet: 'A project' }]
 */
export function parseWikipediaOpenSearch(payload: unknown): PublicSearchHit[] {
  if (!Array.isArray(payload) || payload.length < 4)
    return []
  const titles = payload[1]
  const descriptions = payload[2]
  const urls = payload[3]
  if (!Array.isArray(titles) || !Array.isArray(descriptions) || !Array.isArray(urls))
    return []

  const hits: PublicSearchHit[] = []
  for (let index = 0; index < titles.length && hits.length < 5; index += 1) {
    const title = titles[index]
    const url = urls[index]
    const description = descriptions[index]
    if (typeof title !== 'string' || typeof url !== 'string' || !url.startsWith('https://'))
      continue
    hits.push({
      title: title.slice(0, 180),
      url,
      snippet: (typeof description === 'string' && description.trim() ? description : title).slice(0, 400),
    })
  }
  return hits
}

/** Searches public Wikipedia pages. One query, one request, one timeout. */
export async function searchWikipedia(query: string, fetchImpl: typeof fetch, signal?: AbortSignal): Promise<PublicSearchResult> {
  const trimmed = query.trim()
  if (trimmed.length < 2 || trimmed.length > 200)
    return { status: 'INVALID_INPUT', query: trimmed, message: 'A public search needs 2 to 200 characters.' }

  const timeout = AbortSignal.timeout(searchTimeoutMs)
  const combined = signal ? AbortSignal.any([signal, timeout]) : timeout
  try {
    const response = await fetchImpl(wikipediaSearchUrl(trimmed), { signal: combined })
    if (!response.ok)
      return { status: 'UNAVAILABLE', query: trimmed, message: `Public search returned ${response.status}.` }
    const hits = parseWikipediaOpenSearch(await response.json())
    return { status: 'ok', query: trimmed, hits }
  }
  catch (error) {
    const timedOut = error instanceof Error && error.name === 'TimeoutError'
    return {
      status: timedOut ? 'TIMEOUT' : 'UNAVAILABLE',
      query: trimmed,
      message: timedOut ? 'Public search timed out.' : 'Public search is unavailable.',
    }
  }
}
